import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon, type IconName } from '@/components/icons';

/**
 * HaiSelect — 全站單選下拉(R393 T3;CIS「元件 · 下拉式選單」**禁用原生 `<select>`**)
 * -----------------------------------------------------------------------------
 * 外觀 1:1 沿用 D3 `專案首頁.html` 的 `.pj-dd` / `.pj-ddmenu`(CIS 00:以原始碼為準):
 *   - `variant="field"`(預設)= 表單欄位 `.pj-ddbtn`:padding 10/12、圓角 11、框 hairline、字 13
 *   - `variant="toolbar"` = 工具列排序/篩選 `.pj-sortbtn`:高 34、圓角 8、字 12.5,值用 `<b>` 深字
 *   - 選單:白底圓角 12;選中 `.on` = teal-50 底 + teal-700 字;右側補充字 `hint` 走 `<b>`(11px)
 *   - 點外面與 ESC 皆可關(ESC 走 `escapeLayer`:在燈箱裡展開時,ESC 只收下拉、不關燈箱)
 *
 * ## 三條與原生 `<select>` 不同、但刻意的行為
 * 1. **值對不上任何選項 → 顯 `placeholder`,不自己挑**(R345:React 對 controlled `<select>`
 *    在 value 對不上時會選第一個未 disabled 的 option = 顯示一個沒人選過的值)。
 *    `data-value` 也照實回空字串。
 * 2. **選單 portal 到 body、`position: fixed`** —— 表格 / 燈箱 body / 側面板都是 `overflow` 容器,
 *    absolute 的選單會被裁掉(原生 select 沒有這個問題,換掉它不能多出這個坑)。
 * 3. 選了同一個值不觸發 `onChange`(原生 select 也不會)。
 *
 * ## 測試
 * 觸發鈕 `data-testid={testId}`、`data-value={目前值}`;選單 `data-testid={`${testId}-menu`}`;
 * 選項 `data-value`(測試用 `src/test/haiSelect.ts` 的 `pickHaiSelect` 一次點完)。
 */

export interface HaiSelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** 右側補充字(D3 `.pj-ddmenu b`:計數 / 單位)。 */
  hint?: ReactNode;
  disabled?: boolean;
  /** 群組標題(連續同名的選項歸成一組;= `<optgroup>`)。 */
  group?: string;
  title?: string;
  /** 打字跳選用的文字;`label` 是字串時自動取。 */
  searchText?: string;
}

export interface HaiSelectProps<V extends string = string> {
  value: V | '' | null | undefined;
  options: ReadonlyArray<HaiSelectOption<V>>;
  onChange: (value: V) => void;
  /** 值對不上任何選項時顯示(R345)。 */
  placeholder?: ReactNode;
  variant?: 'field' | 'toolbar';
  /** `sm` = 表格列內 / 分頁列這類窄位(高 30、字 12.5)。 */
  size?: 'md' | 'sm';
  /** 工具列前綴(「排序:」),值接在後面用 `<b>`。 */
  prefix?: ReactNode;
  icon?: IconName;
  disabled?: boolean;
  id?: string;
  title?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** 錯誤態(玫瑰框)。button 不支援 `aria-invalid`(jsx-a11y)→ 走 `data-invalid` + 錯誤文字另以 aria-describedby 連。 */
  invalid?: boolean;
  'aria-describedby'?: string;
  'data-testid'?: string;
  /** 外層(`.pj-dd`)樣式 —— 寬度一般寫在這裡。 */
  style?: CSSProperties;
  /** 觸發鈕額外樣式(錯誤框等)。 */
  triggerStyle?: CSSProperties;
  menuMinWidth?: number;
  menuMaxHeight?: number;
}

const MENU_GAP = 6;
const MENU_MAX_HEIGHT = 280;
const OPTION_EST_HEIGHT = 36;

function textOf(o: HaiSelectOption<string>): string {
  if (o.searchText) return o.searchText;
  return typeof o.label === 'string' || typeof o.label === 'number' ? String(o.label) : '';
}

interface MenuPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

export function HaiSelect<V extends string = string>({
  value,
  options,
  onChange,
  placeholder,
  variant = 'field',
  size = 'md',
  prefix,
  icon,
  disabled = false,
  id,
  title,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  invalid = false,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
  style,
  triggerStyle,
  menuMinWidth,
  menuMaxHeight = MENU_MAX_HEIGHT,
}: HaiSelectProps<V>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const typeAhead = useRef<{ buf: string; at: number }>({ buf: '', at: 0 });
  const listId = useId();

  const selectedIndex = useMemo(
    () => (value == null || value === '' ? options.findIndex((o) => o.value === '') : options.findIndex((o) => o.value === value)),
    [options, value],
  );
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const enabledIndices = useMemo(
    () => options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0),
    [options],
  );

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEscapeLayer(() => close(true), open);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const est = Math.min(menuMaxHeight, options.length * OPTION_EST_HEIGHT + 12);
    const below = vh - r.bottom - MENU_GAP - 8;
    const above = r.top - MENU_GAP - 8;
    const up = below < est && above > below;
    const width = Math.max(r.width, menuMinWidth ?? 0);
    setPos(
      up
        ? { left: r.left, width, bottom: vh - r.top + MENU_GAP, maxHeight: Math.max(120, Math.min(menuMaxHeight, above)) }
        : { left: r.left, width, top: r.bottom + MENU_GAP, maxHeight: Math.max(120, Math.min(menuMaxHeight, below)) },
    );
  }, [menuMaxHeight, menuMinWidth, options.length]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    let raf = 0;
    const onMove = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        place();
      });
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, place]);

  // 點外面就關(選單在 portal 裡,兩個容器都要排除)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open, close]);

  // 展開後把焦點交給目前那一項(鍵盤可直接上下移)。選單要等 `pos` 量好才畫 →
  // 依賴要含「選單已就位」,不然第一次 commit 時 option 還不存在、之後也不會再跑。
  const menuReady = open && pos !== null;
  useEffect(() => {
    if (!menuReady || active < 0) return;
    optionRefs.current[active]?.focus();
  }, [menuReady, active]);

  const openMenu = (prefer: 'selected' | 'first' | 'last' = 'selected') => {
    if (disabled || enabledIndices.length === 0) return;
    const start =
      prefer === 'last'
        ? enabledIndices[enabledIndices.length - 1]
        : prefer === 'selected' && selectedIndex >= 0 && !options[selectedIndex]?.disabled
          ? selectedIndex
          : enabledIndices[0];
    setActive(start ?? -1);
    setOpen(true);
  };

  const pick = (i: number) => {
    const o = options[i];
    if (!o || o.disabled) return;
    close(true);
    if (o.value !== value) onChange(o.value);
  };

  const move = (delta: 1 | -1) => {
    if (enabledIndices.length === 0) return;
    const at = enabledIndices.indexOf(active);
    const next = at < 0 ? 0 : Math.min(enabledIndices.length - 1, Math.max(0, at + delta));
    setActive(enabledIndices[next] ?? -1);
  };

  const jumpByChar = (ch: string) => {
    const now = Date.now();
    const st = typeAhead.current;
    st.buf = now - st.at > 700 ? ch : st.buf + ch;
    st.at = now;
    const needle = st.buf.toLocaleLowerCase();
    const from = Math.max(0, enabledIndices.indexOf(active));
    const order = [...enabledIndices.slice(from + (st.buf.length === 1 ? 1 : 0)), ...enabledIndices.slice(0, from + 1)];
    const hit = order.find((i) => textOf(options[i] as HaiSelectOption<string>).toLocaleLowerCase().startsWith(needle));
    if (hit !== undefined) setActive(hit);
  };

  const onTriggerKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu('selected');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu('selected');
    } else if (e.key === 'End') {
      e.preventDefault();
      openMenu('last');
    } else if (e.key === 'Home') {
      e.preventDefault();
      openMenu('first');
    }
  };

  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(enabledIndices[0] ?? -1);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(enabledIndices[enabledIndices.length - 1] ?? -1);
    } else if (e.key === 'Tab') {
      close(false);
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && e.key !== ' ') {
      jumpByChar(e.key);
    }
  };

  const sm = size === 'sm';
  const shown = selected ? selected.label : placeholder ?? '';
  const isPlaceholder = !selected;

  // 只放幾何;框線 / 底色 / 字色的 hover · open · disabled 狀態在 global.css `.hai-dd-btn`(inline 會蓋掉 :hover)。
  const triggerBase: CSSProperties =
    variant === 'toolbar'
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          height: sm ? 30 : 34,
          padding: '0 12px',
          borderRadius: 8,
          fontSize: 12.5,
          whiteSpace: 'nowrap',
        }
      : {
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: sm ? '6px 10px' : '10px 12px',
          borderRadius: sm ? 8 : 11,
          fontSize: sm ? 12.5 : 13,
          minHeight: sm ? 30 : undefined,
        };

  const groupsSeen = new Set<string>();

  return (
    <div
      ref={wrapRef}
      className={`hai-dd${open ? ' open' : ''}`}
      style={{ position: 'relative', minWidth: 0, ...style }}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`hai-dd-btn ${variant === 'toolbar' ? 'hai-dd-btn--toolbar' : 'hai-dd-btn--field'}${isPlaceholder ? ' is-ph' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        data-invalid={invalid ? 'true' : undefined}
        disabled={disabled}
        title={title}
        data-testid={testId}
        data-value={selected ? selected.value : ''}
        onClick={() => (open ? close(false) : openMenu('selected'))}
        onKeyDown={onTriggerKey}
        style={{
          ...triggerBase,
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...triggerStyle,
        }}
      >
        {icon && (
          <span aria-hidden className="hai-dd-ic" style={{ display: 'flex', color: 'var(--ink-600)', flexShrink: 0 }}>
            <HaiIcon name={icon} size={14} />
          </span>
        )}
        {variant === 'toolbar' ? (
          <>
            {prefix}
            <b style={{ fontWeight: 700, color: 'var(--ink-300)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {shown}
            </b>
          </>
        ) : (
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown}</span>
        )}
        <span
          aria-hidden
          className="hai-dd-ic"
          style={{
            display: 'flex',
            marginLeft: 'auto',
            color: 'var(--ink-600)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.12s',
          }}
        >
          <HaiIcon name="cv" size={13} />
        </span>
      </button>
      {open &&
        pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabel ? undefined : ariaLabelledBy}
            data-testid={testId ? `${testId}-menu` : undefined}
            onKeyDown={onMenuKey}
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              minWidth: pos.width,
              maxWidth: Math.max(pos.width, 360),
              maxHeight: pos.maxHeight,
              overflowY: 'auto',
              boxSizing: 'border-box',
              padding: 6,
              background: 'var(--surface-pop)',
              border: '1px solid var(--border-pop)',
              borderRadius: 12,
              boxShadow: 'var(--shadow-lg)',
              fontFamily: 'var(--font-sans)',
              zIndex: 1050,
            }}
          >
            {options.map((o, i) => {
              const on = i === selectedIndex;
              const header = o.group && !groupsSeen.has(o.group) ? o.group : null;
              if (o.group) groupsSeen.add(o.group);
              return (
                <div key={`${o.group ?? ''}::${o.value}`} role="presentation">
                  {header && (
                    <div
                      role="presentation"
                      style={{ padding: '8px 10px 4px', fontSize: 11, fontWeight: 700, color: 'var(--ink-500)' }}
                    >
                      {header}
                    </div>
                  )}
                  <button
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={on}
                    aria-disabled={o.disabled || undefined}
                    disabled={o.disabled}
                    tabIndex={i === active ? 0 : -1}
                    title={o.title}
                    className={`hai-dd-item${on ? ' on' : ''}`}
                    data-value={o.value}
                    data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                    onMouseEnter={() => !o.disabled && setActive(i)}
                    onClick={() => pick(i)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      border: 'none',
                      borderRadius: 8,
                      fontFamily: 'inherit',
                      fontSize: 13,
                      // 底色 / 字色 / 停用態一律交給 global.css `.hai-dd-item`(inline 會蓋掉 :hover 與 .on)
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
                    {o.hint != null && (
                      <b style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                        {o.hint}
                      </b>
                    )}
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
